import { supabase } from '../../config/supabase/client';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export type JambSubject = {
  id: string;
  name: string;
  code: string | null;
  is_compulsory: boolean;
  is_active: boolean;
  sort_order: number;
};

export type JambQuestion = {
  id: string;
  subject_id: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  difficulty: string;
  topic: string | null;
  year: number | null;
  source: string | null;
  explanation?: string | null;
};

export type JambRegistrationSubject = {
  subject_id: string;
  is_compulsory: boolean;
  subject: JambSubject;
};

export type JambRegistration = {
  id: string;
  student_id: string;
  created_at: string;
  jamb_registration_subjects: JambRegistrationSubject[];
};

export type JambAttempt = {
  id: string;
  registration_id: string;
  subject_id: string;
  started_at: string;
  submitted_at: string | null;
  score: number | null;
  correct_count: number | null;
  wrong_count?: number | null;
  unanswered_count?: number | null;
  question_count: number | null;
  duration_seconds: number | null;
  question_ids?: string[] | null;
  subject?: {
    id: string;
    name: string;
    code: string | null;
  };
};

export type JambReviewQuestion = JambQuestion & {
  selectedOption: string | null;
  correctOption: string | null;
  isCorrect: boolean;
};

export type JambSubmitResult = {
  score: number;
  correct: number;
  wrong: number;
  unanswered: number;
  question_count: number;
  duration_seconds: number;
  topic_breakdown: Array<{
    topic: string;
    total: number;
    correct: number;
    wrong: number;
    unanswered: number;
    accuracy: number;
  }>;
  reviewQuestions: JambReviewQuestion[];
};

const COOLDOWN_HOURS = 48;

/* ------------------------------------------------------------------ */
/* Student                                                           */
/* ------------------------------------------------------------------ */

async function getStudent() {
  const { data: auth, error: authError } =
    await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  const user = auth?.user;

  if (!user) {
    throw new Error('Not authenticated.');
  }

  const { data, error } = await supabase
    .from('students')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error) {
    throw error;
  }

  return data;
}

/* ------------------------------------------------------------------ */
/* Subjects                                                           */
/* ------------------------------------------------------------------ */

async function getSubjects(): Promise<JambSubject[]> {
  const { data, error } = await supabase
    .from('jamb_subjects')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) {
    throw error;
  }

  return (data || []) as JambSubject[];
}

/* ------------------------------------------------------------------ */
/* Registration                                                       */
/* ------------------------------------------------------------------ */

async function getRegistration(
  studentId: string,
): Promise<JambRegistration | null> {
  const { data, error } = await supabase
    .from('jamb_registrations')
    .select(
      `
        id,
        student_id,
        created_at,
        jamb_registration_subjects(
          subject_id,
          is_compulsory,
          subject:jamb_subjects(
            id,
            name,
            code,
            is_compulsory,
            is_active,
            sort_order
          )
        )
      `,
    )
    .eq('student_id', studentId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data || null) as JambRegistration | null;
}

async function register(
  studentId: string,
  subjectIds: string[],
) {
  const { data: reg, error: regError } = await supabase
    .from('jamb_registrations')
    .insert({
      student_id: studentId,
    })
    .select()
    .single();

  if (regError) {
    throw regError;
  }

  const rows = subjectIds.map((id) => ({
    registration_id: reg.id,
    subject_id: id,
  }));

  const { error: subjError } = await supabase
    .from('jamb_registration_subjects')
    .insert(rows);

  if (subjError) {
    throw subjError;
  }

  const full = await getRegistration(studentId);

  return full!;
}

/* ------------------------------------------------------------------ */
/* Attempts                                                           */
/* ------------------------------------------------------------------ */

async function getAttempts(
  registrationId: string,
): Promise<JambAttempt[]> {
  const { data, error } = await supabase
    .from('jamb_attempts')
    .select(
      `
        id,
        registration_id,
        subject_id,
        started_at,
        submitted_at,
        score,
        correct_count,
        wrong_count,
        unanswered_count,
        question_count,
        duration_seconds,
        question_ids,
        subject:jamb_subjects(
          id,
          name,
          code
        )
      `,
    )
    .eq('registration_id', registrationId)
    .order('started_at', {
      ascending: false,
    });

  if (error) {
    throw error;
  }

  const attempts = (data || []).map(
    (row: any) => ({
      ...row,
      subject: Array.isArray(row.subject)
        ? row.subject[0]
        : row.subject,
    }),
  );

  return attempts as JambAttempt[];
}

/* ------------------------------------------------------------------ */
/* Cooldown                                                           */
/* ------------------------------------------------------------------ */

async function getCooldownInfo(
  registrationId: string,
  subjectId: string,
) {
  const { data, error } = await supabase
    .from('jamb_attempts')
    .select(
      'id,submitted_at,started_at,question_ids',
    )
    .eq('registration_id', registrationId)
    .eq('subject_id', subjectId)
    .not('submitted_at', 'is', null)
    .order('submitted_at', {
      ascending: false,
    })
    .limit(1);

  if (error) {
    throw error;
  }

  const last: any = data?.[0];

  if (!last?.submitted_at) {
    return {
      canStart: true,
      nextAvailableAt: null as Date | null,
      lastAttemptAt: null as Date | null,
      previousQuestionIds: [] as string[],
    };
  }

  const lastAt = new Date(
    last.submitted_at,
  );

  const next = new Date(
    lastAt.getTime() +
      COOLDOWN_HOURS * 60 * 60 * 1000,
  );

  const now = new Date();

  return {
    canStart: now >= next,
    nextAvailableAt: next,
    lastAttemptAt: lastAt,
    previousQuestionIds:
      (last.question_ids || []) as string[],
  };
}

/* ------------------------------------------------------------------ */
/* Previously used questions                                          */
/* ------------------------------------------------------------------ */

async function getUsedQuestionIds(
  registrationId: string,
  subjectId: string,
): Promise<string[]> {
  const { data, error } = await supabase
    .from('jamb_attempts')
    .select('question_ids')
    .eq('registration_id', registrationId)
    .eq('subject_id', subjectId);

  if (error) {
    throw error;
  }

  const set = new Set<string>();

  (data || []).forEach((row: any) => {
    const ids = Array.isArray(row.question_ids)
      ? row.question_ids
      : [];

    ids.forEach((id: string) => {
      if (id) {
        set.add(id);
      }
    });
  });

  return Array.from(set);
}

/* ------------------------------------------------------------------ */
/* QUESTION POOL                                                      */
/* ------------------------------------------------------------------ */

async function getUnseenQuestions(
  subjectId: string,
  excludeIds: string[],
  limit: number,
): Promise<JambQuestion[]> {
  const safeLimit = Math.max(
    1,
    Math.floor(limit),
  );

  /*
   * Remove duplicate IDs from the exclusion list.
   */
  const uniqueExcludeIds = Array.from(
    new Set(
      excludeIds.filter(
        (id) => typeof id === 'string' && id.length > 0,
      ),
    ),
  );

  let query = supabase
    .from('jamb_questions')
    .select(
      `
        id,
        subject_id,
        question_text,
        option_a,
        option_b,
        option_c,
        option_d,
        difficulty,
        topic,
        year,
        source,
        explanation
      `,
    )
    .eq('subject_id', subjectId)
    .eq('is_active', true);

  /*
   * IMPORTANT:
   *
   * We fetch more than the requested amount when possible.
   * Supabase then returns real database rows which we shuffle
   * locally. We do NOT reuse one question object for multiple
   * navigator positions.
   */
  if (uniqueExcludeIds.length > 0) {
    query = query.not(
      'id',
      'in',
      `(${uniqueExcludeIds.join(',')})`,
    );
  }

  const { data, error } = await query
    .order('created_at', {
      ascending: true,
    })
    .limit(safeLimit);

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  /*
   * Convert to question objects.
   */
  const fetchedQuestions =
    data as JambQuestion[];

  /*
   * GUARANTEE UNIQUE QUESTION IDs.
   */
  const uniqueQuestions: JambQuestion[] = [];
  const seenIds = new Set<string>();

  for (const question of fetchedQuestions) {
    if (!question?.id) {
      continue;
    }

    if (seenIds.has(question.id)) {
      continue;
    }

    seenIds.add(question.id);
    uniqueQuestions.push(question);
  }

  /*
   * Shuffle the actual question records.
   */
  for (
    let i = uniqueQuestions.length - 1;
    i > 0;
    i -= 1
  ) {
    const j = Math.floor(
      Math.random() * (i + 1),
    );

    const temporary =
      uniqueQuestions[i];

    uniqueQuestions[i] =
      uniqueQuestions[j];

    uniqueQuestions[j] =
      temporary;
  }

  /*
   * Diagnostic information.
   *
   * This will prove whether the service is returning
   * different questions.
   */
  console.log(
    '[JAMB CBT] Questions loaded:',
    uniqueQuestions.map(
      (question, index) => ({
        number: index + 1,
        id: question.id,
        text: question.question_text,
      }),
    ),
  );

  return uniqueQuestions.slice(
    0,
    safeLimit,
  );
}

/* ------------------------------------------------------------------ */
/* Total active questions                                             */
/* ------------------------------------------------------------------ */

async function getTotalActiveQuestions(
  subjectId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from('jamb_questions')
    .select('*', {
      count: 'exact',
      head: true,
    })
    .eq('subject_id', subjectId)
    .eq('is_active', true);

  if (error) {
    throw error;
  }

  return count || 0;
}

/* ------------------------------------------------------------------ */
/* Reset question pool                                                */
/* ------------------------------------------------------------------ */

async function resetQuestionPool(
  registrationId: string,
  subjectId: string,
) {
  /*
   * IMPORTANT:
   *
   * Only clear the question_ids for attempts belonging
   * to this registration and subject.
   */
  const { error } = await supabase
    .from('jamb_attempts')
    .update({
      question_ids: [],
    })
    .eq('registration_id', registrationId)
    .eq('subject_id', subjectId)
    .is('submitted_at', null);

  if (error) {
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* Start attempt                                                      */
/* ------------------------------------------------------------------ */

async function startAttempt(
  registrationId: string,
  subjectId: string,
  questionIds: string[] = [],
) {
  /*
   * Ensure the attempt receives unique question IDs.
   */
  const uniqueQuestionIds = Array.from(
    new Set(
      questionIds.filter(
        (id) =>
          typeof id === 'string' &&
          id.length > 0,
      ),
    ),
  );

  if (!uniqueQuestionIds.length) {
    throw new Error(
      'Cannot start CBT without questions.',
    );
  }

  const { data, error } = await supabase
    .from('jamb_attempts')
    .insert({
      registration_id: registrationId,
      subject_id: subjectId,
      started_at: new Date().toISOString(),
      question_ids: uniqueQuestionIds,
      question_count:
        uniqueQuestionIds.length,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

/* ------------------------------------------------------------------ */
/* Question count                                                     */
/* ------------------------------------------------------------------ */

async function setAttemptQuestionCount(
  attemptId: string,
  count: number,
) {
  const { error } = await supabase
    .from('jamb_attempts')
    .update({
      question_count: count,
    })
    .eq('id', attemptId);

  if (error) {
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* Submit attempt                                                     */
/* ------------------------------------------------------------------ */

async function submitAttempt(
  attemptId: string,
  answers: Record<string, string>,
  durationSeconds: number,
): Promise<JambSubmitResult> {
  /*
   * IMPORTANT:
   *
   * The database function uses p_duration_seconds,
   * NOT p_duration.
   */
  const { data, error } =
    await supabase.rpc(
      'submit_jamb_attempt',
      {
        p_attempt_id: attemptId,
        p_answers: answers,
        p_duration_seconds:
          durationSeconds,
      },
    );

  if (error) {
    throw error;
  }

  const result =
    data as JambSubmitResult;

  return {
    score: Number(
      result?.score || 0,
    ),

    correct: Number(
      result?.correct || 0,
    ),

    wrong: Number(
      result?.wrong || 0,
    ),

    unanswered: Number(
      result?.unanswered || 0,
    ),

    question_count: Number(
      result?.question_count || 0,
    ),

    duration_seconds: Number(
      result?.duration_seconds || 0,
    ),

    topic_breakdown:
      Array.isArray(
        result?.topic_breakdown,
      )
        ? result.topic_breakdown
        : [],

    reviewQuestions:
      Array.isArray(
        result?.reviewQuestions,
      )
        ? result.reviewQuestions.map(
            (question) => ({
              ...question,

              selectedOption:
                question.selectedOption
                  ? String(
                      question.selectedOption,
                    ).toUpperCase()
                  : null,

              correctOption:
                question.correctOption
                  ? String(
                      question.correctOption,
                    ).toUpperCase()
                  : null,

              isCorrect:
                Boolean(
                  question.isCorrect,
                ),
            }),
          )
        : [],
  };
}

/* ------------------------------------------------------------------ */
/* Generic question getter                                            */
/* ------------------------------------------------------------------ */

async function getQuestions(
  subjectId: string,
  limit: number,
): Promise<JambQuestion[]> {
  return getUnseenQuestions(
    subjectId,
    [],
    limit,
  );
}

/* ------------------------------------------------------------------ */
/* Export                                                             */
/* ------------------------------------------------------------------ */

export const jambCbtService = {
  getStudent,
  getSubjects,
  getRegistration,
  register,
  getAttempts,
  getCooldownInfo,
  getUsedQuestionIds,
  getUnseenQuestions,
  getTotalActiveQuestions,
  resetQuestionPool,
  startAttempt,
  setAttemptQuestionCount,
  submitAttempt,
  getQuestions,
};

export default jambCbtService;